import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import type { IColors } from '@/types/setting';
import store from '@/store';
import { UserModule } from './user';
import { ApplicationDefaults } from '@/constants/ApplicationDefaults';
import { mimicLighterOpacity } from '@/utils/color';
import type { PdfHeader } from '@/types/PdfHeader';
import useAppMetrics from '@/composables/useAppMetrics';
import { findMatchingMark } from '@/utils/findMatchingMark';
import { ACCEPTED_TRACKED_TITLES } from '@/types/analytics';
import type { CSSProperties } from 'vue';

export interface ISettingsState {
  printTitle: string;
  colors: IColors;
  printSubtitle: string;
  printHeaderRightSideItems: string[];
  pdfSectionDividerColor: string;
  regimeName?: string;
}

const DEBUG = false;

/**
 * Developers are discouraged in adding new properties here as Vue3 migration will require the removal of stores.
 */
/**
 * Developers are discouraged in adding new properties here as Vue3 migration will require the removal of stores.
 */
@Module({ dynamic: true, store, name: 'Settings' })
class Settings extends VuexModule implements ISettingsState {
  public colors: IColors = {};

  public portfolioColor = ApplicationDefaults.colors.Primary;
  public activeReturnColor = ApplicationDefaults.colors.ActiveReturn;
  public defaultChildColor = mimicLighterOpacity(ApplicationDefaults.colors.Info);

  public pin20Strategies = 20;
  public pin50Strategies = 50;
  public maxNumStrategiesPerSubportfolio = 100;

  @Mutation
  public SET_COLORS(colors: IColors): void {
    this.colors = colors;
  }

  // ================== PDF  =======================
  public printTitle = '';
  public printSubtitle = '';
  public printAnalysisItemAttributes = '';
  public regimeName = '';
  public printHeaderRightSideItems: string[] = [];
  public style: { [key: string]: string } = {};

  /**
   * Mark this boolean as true in order to completely replace the PDF header,
   * as opposed to working with our default header
   */
  public shouldUseWhitelabelCustomPdfHeaderComponent = false;
  @Action({ rawError: true })
  public async ChangeShouldUseWhitelabelCustomPdfHeaderComponent(newVal: boolean): Promise<void> {
    this.CHANGE_SHOULD_USE_WHITELABEL_CUSTOM_PDF_HEADER_COMPONENT(newVal);
  }
  @Mutation
  private CHANGE_SHOULD_USE_WHITELABEL_CUSTOM_PDF_HEADER_COMPONENT(newVal: boolean): void {
    this.shouldUseWhitelabelCustomPdfHeaderComponent = newVal;
  }

  /**
   * Mark this boolean as true in order to completely replace the PDF footer,
   * as opposed to working with our default footer
   */
  public shouldUseWhitelabelCustomPdfFooterComponent = false;
  @Action({ rawError: true })
  public async ChangeShouldUseWhitelabelCustomPdfFooterComponent(newVal: boolean): Promise<void> {
    this.CHANGE_SHOULD_USE_WHITELABEL_CUSTOM_PDF_FOOTER_COMPONENT(newVal);
  }
  @Mutation
  private CHANGE_SHOULD_USE_WHITELABEL_CUSTOM_PDF_FOOTER_COMPONENT(newVal: boolean): void {
    this.shouldUseWhitelabelCustomPdfFooterComponent = newVal;
  }

  /**
   * When working with our default header, you can manipulate the style
   * of the title header with the following
   */
  public pdfHeaderTitleStyleCustom: CSSProperties | null = null;
  public get pdfHeaderTitleStyle(): CSSProperties | null {
    return this.pdfHeaderTitleStyleCustom;
  }
  @Action({ rawError: true })
  public async ChangePdfHeaderTitleStyleCustom(newVal: CSSProperties | null): Promise<void> {
    this.CHANGE_PDF_HEADER_TITLE_STYLE_CUSTOM(newVal);
  }
  @Mutation
  private CHANGE_PDF_HEADER_TITLE_STYLE_CUSTOM(newVal: CSSProperties | null): void {
    this.pdfHeaderTitleStyleCustom = newVal;
  }

  /**
   * When working with our default header, you can manipulate the style
   * of the subtitle header with the following
   */
  public pdfHeaderSubtitleStyleCustom: CSSProperties | null = null;
  public get pdfHeaderSubtitleStyle(): CSSProperties | null {
    return this.pdfHeaderSubtitleStyleCustom;
  }
  @Action({ rawError: true })
  public async ChangePdfHeaderSubtitleStyleCustom(newVal: CSSProperties | null): Promise<void> {
    this.CHANGE_PDF_HEADER_SUBTITLE_STYLE_CUSTOM(newVal);
  }
  @Mutation
  private CHANGE_PDF_HEADER_SUBTITLE_STYLE_CUSTOM(newVal: CSSProperties | null): void {
    this.pdfHeaderSubtitleStyleCustom = newVal;
  }

  /**
   * When working with our default header, you can manipulate the style
   * of the parent object by modifying this object.
   */
  public pdfHeaderStyleCustom: CSSProperties | null = null;
  public get pdfPageHeaderStyle(): CSSProperties {
    if (this.pdfHeaderStyleCustom) return this.pdfHeaderStyleCustom;

    let backgroundColor = ApplicationDefaults.pdf.navbarColor;
    if (UserModule.pdfParameters && UserModule.pdfParameters.navbarColor !== '') {
      backgroundColor = UserModule.pdfParameters.navbarColor;
    }

    return {
      'background-color': `${backgroundColor} !important`,
    };
  }
  @Action({ rawError: true })
  public async ChangePdfHeaderStyleCustom(newVal: CSSProperties): Promise<void> {
    this.CHANGE_PDF_HEADER_STYLE_CUSTOM(newVal);
  }
  @Mutation
  private CHANGE_PDF_HEADER_STYLE_CUSTOM(newVal: CSSProperties): void {
    this.pdfHeaderStyleCustom = newVal;
  }

  /**
   * Mark this boolean as true in order to completely replace the PDF footer,
   * as opposed to working with our default footer
   */
  public pdfHeaderImgSrc: string | null = null;
  @Action({ rawError: true })
  public async ChangePdfHeaderImgSrc(newVal: string | null): Promise<void> {
    this.CHANGE_PDF_HEADER_IMG_SRC(newVal);
  }
  @Mutation
  private CHANGE_PDF_HEADER_IMG_SRC(newVal: string | null): void {
    this.pdfHeaderImgSrc = newVal;
  }

  /**
   * Manipulate the style of the accent bar for our default header
   * By setting it to {} you can make it disappear completely
   */
  public pdfHeaderAccentBarStyleCustom: CSSProperties | null = null;
  public get pdfPageHeaderAccentBarStyle(): CSSProperties {
    if (this.pdfHeaderAccentBarStyleCustom) return this.pdfHeaderAccentBarStyleCustom;

    let color = ApplicationDefaults.pdf.navbarLineColor;
    if (UserModule.pdfParameters) {
      color = UserModule.pdfParameters.navbarLineColor;
    }

    return {
      'border-right': `3px solid ${color} !important`,
    };
  }
  @Action({ rawError: true })
  public async ChangePdfHeaderAccentBarStyleCustom(newVal: CSSProperties): Promise<void> {
    this.CHANGE_PDF_HEADER_ACCENT_BAR_STYLE_CUSTOM(newVal);
  }
  @Mutation
  private CHANGE_PDF_HEADER_ACCENT_BAR_STYLE_CUSTOM(newVal: CSSProperties): void {
    this.pdfHeaderAccentBarStyleCustom = newVal;
  }

  /**
   * Change the style of the text in our default PDF header
   */
  public pdfHeaderTextStyleCustom: CSSProperties | null = null;
  public get pdfPageHeaderTextStyle(): CSSProperties {
    if (this.pdfHeaderTextStyleCustom) return this.pdfHeaderTextStyleCustom;

    let color = ApplicationDefaults.pdf.navbarTextColor;

    if (UserModule.pdfParameters && UserModule.pdfParameters.navbarTextColor !== '') {
      color = UserModule.pdfParameters.navbarTextColor;
    }

    return {
      color: `${color} !important`,
    };
  }
  @Action({ rawError: true })
  public async ChangePdfHeaderTextStyleCustom(newVal: CSSProperties): Promise<void> {
    this.CHANGE_PDF_HEADER_TEXT_STYLE_CUSTOM(newVal);
  }
  @Mutation
  private CHANGE_PDF_HEADER_TEXT_STYLE_CUSTOM(newVal: CSSProperties): void {
    this.pdfHeaderTextStyleCustom = newVal;
  }

  /**
   * This logo will be used by HSBC. It will change depending on which
   * strategy factsheet is being generated. This logic is handled in
   * the formatPdfHeaderWhitelabel function in
   * src/views/factsheets/StrategyFactsheetPdf.vue
   */
  public pdfPageHeaderCustomDynamicLogo: string | null = null;
  @Action({ rawError: true })
  public async ChangePdfHeaderCustomDynamicLogo(newVal: string): Promise<void> {
    this.CHANGE_PDF_HEADER_CUSTOM_DYNAMIC_LOGO(newVal);
  }
  @Mutation
  private CHANGE_PDF_HEADER_CUSTOM_DYNAMIC_LOGO(newVal: string): void {
    this.pdfPageHeaderCustomDynamicLogo = newVal;
  }

  public get pdfSectionDividerColor(): string {
    if (UserModule.pdfParameters && UserModule.pdfParameters.headerLineColor !== '') {
      return UserModule.pdfParameters.headerLineColor;
    }

    return ApplicationDefaults.pdf.headerLineColor;
  }

  public get pdfSectionHeaderTextColor(): string {
    if (UserModule.pdfParameters && UserModule.pdfParameters.headerColor !== '') {
      return UserModule.pdfParameters.headerColor;
    }

    return ApplicationDefaults.colors.Primary;
  }

  @Action({ rawError: true })
  public async ChangePrintHeader(newHeader: PdfHeader): Promise<void> {
    this.CHANGE_PRINT_HEADER(newHeader);
  }
  @Mutation
  private CHANGE_PRINT_HEADER(newHeader: PdfHeader): void {
    if (newHeader.title) this.printTitle = newHeader.title;

    if (newHeader.subtitle) this.printSubtitle = newHeader.subtitle;

    if (newHeader.analysisItemAttributes) this.printAnalysisItemAttributes = newHeader.analysisItemAttributes;

    if (newHeader.currentRegimeName) this.regimeName = newHeader.currentRegimeName;

    if (newHeader.rightSideItems) this.printHeaderRightSideItems = newHeader.rightSideItems;
  }

  /**
   * We set a manual height limit on the description field
   * because it can get so long. This allows us to make sure
   * that everything else that comes after the description
   * (i.e., the rest of the PDF) is laid out nicely.
   *
   * Because morningstar funds have more static data fields,
   * we allow more room for those and subtract 50px from
   * the height here.
   */
  public strategyFactsheetPdfDescriptionHeightLimit = 250;
  public strategyFactsheetPdfDescriptionHeightLimitCustom: number | null = null;

  @Action({ rawError: true })
  public async ChangeStrategyFactsheetDescriptionHeightLimitCustom(newVal: number | null): Promise<void> {
    this.CHANGE_STRATEGY_FACTSHEET_PDF_DESCRIPTION_HEIGHT_LIMIT_CUSTOM(newVal);
  }
  @Mutation
  private CHANGE_STRATEGY_FACTSHEET_PDF_DESCRIPTION_HEIGHT_LIMIT_CUSTOM(newVal: number | null): void {
    this.strategyFactsheetPdfDescriptionHeightLimitCustom = newVal;
  }
  // ================== PDF =======================

  /**
   * First this function checks if a matching mark already exists.
   * If it does, it returns undefined.
   * If it doesn't, it creates a new mark with the given options.
   */
  @Action({ rawError: true })
  public async StartPerformanceMark({
    name,
    options,
  }: {
    name: string;
    options?: PerformanceMarkOptions;
  }): Promise<PerformanceMark | undefined> {
    if (performance.mark === undefined) return;

    if (DEBUG) console.log('StartPerformanceMark', { name, options });

    const match = findMatchingMark({
      name,
      startOrEnd: 'start',
      options,
    });
    // if we find a matching mark, we don't want to create a new start mark with the same options
    // this may occur when watching `useQueries` results
    if (match) return;

    return performance.mark(`${name}-start`, options);
  }

  /**
   * First this function checks if a matching mark already exists.
   * If it does, it returns undefined.
   * If it doesn't, it creates a new mark with the given options.
   */
  @Action({ rawError: true })
  public async EndPerformanceMark({
    name,
    markOptions,
  }: {
    name: string;
    /**
     * options used to identify the matching start mark and identify the end mark
     */
    markOptions?: PerformanceMarkOptions;
  }): Promise<PerformanceEntry | undefined> {
    if (performance.mark === undefined) return;

    const match = findMatchingMark({ name, startOrEnd: 'end', options: markOptions });
    // if we find a matching mark, we don't want to create a new end mark with the same options
    // this may occur when watching `useQueries` results
    if (match) {
      if (DEBUG) console.log('EndPerformanceMark', name, 'match found, returningn early');

      return;
    }

    if (DEBUG) console.log('EndPerformanceMark', name);
    const endMark = performance.mark(`${name}-end`, markOptions);

    const matchingStartMark = findMatchingMark({ name, startOrEnd: 'start', options: markOptions });
    if (!matchingStartMark) {
      if (DEBUG) console.log('no matching start mark found, returning early');

      return;
    }

    const { detail: startDetails } = matchingStartMark;
    const { detail: endDetails } = endMark;
    return this.CreatePerformanceMeasure({
      name,
      startMark: matchingStartMark,
      endMark,
      details: { ...startDetails, ...endDetails },
    });
  }

  @Action({ rawError: true })
  public async CreatePerformanceMeasure({
    name,
    startMark,
    endMark,
    details,
  }: {
    name: string;
    startMark: PerformanceMark;
    endMark: PerformanceMark;
    details?: Record<string, unknown>;
  }): Promise<PerformanceEntry | undefined> {
    const { track } = useAppMetrics();

    if (performance.measure === undefined) {
      return;
    }

    const measure = performance.measure(startMark.name.replace('-start', ''), {
      start: startMark.startTime,
      end: endMark.startTime,
    });
    if (!measure) return;

    if (DEBUG) console.log('CreatePerformanceMeasure', { name, measure, details });

    track(ACCEPTED_TRACKED_TITLES.PERFORMANCE_METRIC_RECORDED, {
      itemName: name,
      duration: measure.duration,
      ...details,
    });

    return measure;
  }
}

export const SettingsModule = getModule(Settings);
